// Mirrored from delivery.js. Duplicating the storage-key prefix rather than importing keeps
// state.js import-free of the full delivery module — the whole point of this file is being
// safe to load from data_hub_forget_user.js and from the bootstrap-time capture path before the
// lazy-loaded analytics chunk has shown up.
const pending_events_storage_key_prefix = 'data-hub:web:pending-events';

function get_persisted_pending_events_storage_key(data_hub_instance) {
	const organization_id = typeof data_hub_instance?.organization_id === 'string' && data_hub_instance.organization_id.trim()
		? data_hub_instance.organization_id.trim()
		: 'shared';
	return `${pending_events_storage_key_prefix}:${organization_id}`;
}

function safely_remove_persisted_pending_events(data_hub_instance) {
	try {
		if (typeof localStorage !== 'undefined') {
			localStorage.removeItem(get_persisted_pending_events_storage_key(data_hub_instance));
		}
	} catch {
		// Storage may be blocked in private or restricted contexts.
	}
}

/**
 * Clears in-memory analytics queues for an instance and removes the persisted retry queue
 * from localStorage. This is intentionally small and exported separately so identity reset /
 * forget-user flows and the consent-denial path in capture() can clear analytics state
 * without importing the full lazy-loaded analytics module.
 *
 * @param {Object} data_hub_instance - DataHub instance that owns analytics state.
 * @returns {void}
 */
export function clear_analytics_queues(data_hub_instance) {
    if (!data_hub_instance?.analytics_state) {
        return;
    }
    data_hub_instance.analytics_state.queue = [];
    data_hub_instance.analytics_state.pre_init_queue = [];
    safely_remove_persisted_pending_events(data_hub_instance);
}

/**
 * Returns true when capture / flush / persist should be a no-op for this instance.
 * Set permanently to true by cleanup_analytics_state (e.g. forget_user). We do not
 * re-enable after consent grants in v1 — a re-consenting user needs a page reload.
 *
 * @param {Object|null|undefined} data_hub_instance
 * @returns {boolean}
 */
export function analytics_captures_disabled(data_hub_instance) {
    return Boolean(data_hub_instance?.analytics_state?.captures_disabled);
}

/**
 * Tears the analytics pipeline down for an instance and permanently blocks future captures.
 * Calls every registered cleanup callback — that removes click/scroll/visibility listeners,
 * disconnects the video MutationObserver, clears the periodic flush interval, and restores
 * window.history.pushState / replaceState — then clears in-memory queues, removes the
 * persisted retry queue from localStorage, and sets a captures_disabled flag so any further
 * data_hub.capture(), flush, or persist_failed_events calls short-circuit without touching
 * the network or local storage. Used by forget_user so a "forget me" click cannot leave
 * analytics writing data afterwards.
 *
 * @param {Object} data_hub_instance - DataHub instance that owns analytics state.
 * @returns {void}
 */
export function cleanup_analytics_state(data_hub_instance) {
    if (!data_hub_instance) {
        return;
    }
    if (!data_hub_instance.analytics_state) {
        data_hub_instance.analytics_state = {};
    }
    const cleanup_callbacks = Array.isArray(data_hub_instance.analytics_state.cleanup_callbacks)
        ? data_hub_instance.analytics_state.cleanup_callbacks
        : [];
    cleanup_callbacks.forEach((cleanup_callback) => {
        try {
            if (typeof cleanup_callback === 'function') {
                cleanup_callback();
            }
        } catch {
            // Cleanup should never break page unload or identity reset paths.
        }
    });
    data_hub_instance.analytics_state.cleanup_callbacks = [];
    data_hub_instance.analytics_state.early_cleanup_callbacks = [];
    data_hub_instance.analytics_state.captures_disabled = true;
    clear_analytics_queues(data_hub_instance);
}